import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrderPaymentProofUrlItem } from "@/lib/api/types";
import { formatAmountValue, formatAbsoluteDate } from "./order-list.utils";

type Props = {
  locale: string;
  proofItems: OrderPaymentProofUrlItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
};

export function PaymentReviewProofViewer({
  locale,
  proofItems,
  selectedIndex,
  onSelect,
}: Props) {
  const t = useTranslations();
  const selected = proofItems[selectedIndex] ?? null;

  if (!selected) {
    return (
      <div
        style={{
          padding: 16,
          borderRadius: "var(--rd-radius-card)",
          border: "1px dashed var(--rd-line-control)",
          fontSize: 14,
          color: "var(--rd-text-secondary)",
        }}
      >
        {t(keys.orders.noProofLabel)}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          position: "relative",
          height: 300,
          width: "100%",
          overflow: "hidden",
          borderRadius: "var(--rd-radius-card)",
          background: "var(--rd-surface-sunken)",
          boxShadow: "var(--rd-shadow-card)",
        }}
      >
        <img
          src={selected.url}
          alt={t(keys.orders.proofUploadedLabel)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>
      <div style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--rd-text-secondary)" }}>
        <span className="rd-num">{formatAmountValue(locale, selected.paidAmount)}</span>
        <span aria-hidden="true" style={{ color: "var(--rd-text-faint)" }}>·</span>
        <span>{formatAbsoluteDate(locale, selected.uploadedAt)}</span>
      </div>
      {proofItems.length > 1 ? (
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
          {proofItems.map((proof, index) => (
            <button
              key={proof.proofId}
              type="button"
              aria-pressed={index === selectedIndex}
              onClick={() => onSelect(index)}
              style={{
                position: "relative",
                width: 64,
                height: 64,
                flex: "none",
                padding: 0,
                overflow: "hidden",
                borderRadius: "var(--rd-radius-md)",
                border: index === selectedIndex ? "2px solid var(--rd-primary)" : "1px solid var(--rd-line-control)",
                background: "var(--rd-surface-sunken)",
                cursor: "pointer",
              }}
            >
              <img
                src={proof.url}
                alt={t(keys.orders.proofUploadedLabel)}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
